/**
 * After Mark lodged (R3 T6b): "Lodged 3 Nov · 5 files" with read-only links
 * to the copies frozen at lodgement. Those copies never change, whatever is
 * edited afterwards. Earlier lodgements (replaced by a later one) stay listed.
 *
 * A bid moved back to Active or Lead keeps its records (and `bids.lodgedAt`,
 * on purpose): they show as history, "Previously lodged 3 Nov · 5 files",
 * folded, so the page doesn't claim the bid is lodged now.
 */

import { useState } from 'react'
import { ChevronRight, Lock } from 'lucide-react'
import { lodgedSummary, shortDateTime } from '@/lib/submission-files-text'
import { cn } from '@/lib/utils'
import type { SubmissionRecord, SubmissionRecordFile } from '@/types/submission-files'

interface LodgedRecordsProps {
  /** Newest first. */
  records: SubmissionRecord[]
  /** The bid is no longer lodged: show every record as history, folded. */
  isHistory?: boolean
  onDownload: (file: SubmissionRecordFile) => void
}

function RecordBlock({
  record,
  isLatest,
  isHistory,
  onDownload,
}: {
  record: SubmissionRecord
  isLatest: boolean
  isHistory: boolean
  onDownload: (file: SubmissionRecordFile) => void
}) {
  const isCurrent = isLatest && !isHistory
  const [isOpen, setIsOpen] = useState(isCurrent)
  const who = record.recordedByName ? ` · Recorded by ${record.recordedByName}` : ''
  return (
    <div data-record-id={record.id}>
      <button
        type="button"
        className="inline-flex items-center gap-1 text-sm hover:text-foreground"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((open) => !open)}
      >
        <ChevronRight aria-hidden className={cn('h-3.5 w-3.5 text-subtle transition-transform', isOpen && 'rotate-90')} />
        <Lock aria-hidden className="h-3.5 w-3.5 text-muted-foreground" />
        <span className={cn(isCurrent && 'font-medium')}>{lodgedSummary(record, undefined, { previous: isHistory })}</span>
        {!isLatest && <span className="text-meta">(replaced)</span>}
      </button>
      {isOpen && (
        <div className="mt-1.5 space-y-1.5 pl-5">
          <p className="text-meta">
            Lodged {shortDateTime(record.lodgedAt)}
            {who}
            {record.reference ? ` · Reference ${record.reference}` : ''}
          </p>
          {record.files.length > 0 && (
            <ul className="space-y-1">
              {record.files.map((file) => (
                <li key={file.id}>
                  <button
                    type="button"
                    className="text-left text-sm text-[hsl(var(--link))] hover:underline"
                    onClick={() => onDownload(file)}
                  >
                    {file.name}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {record.receipt && (
            <p className="text-meta">
              Receipt:{' '}
              <button
                type="button"
                className="text-[hsl(var(--link))] hover:underline"
                onClick={() => onDownload(record.receipt!)}
              >
                {record.receipt.name}
              </button>
            </p>
          )}
          <p className="text-meta">Kept as lodged. Later edits don&apos;t change these copies.</p>
        </div>
      )}
    </div>
  )
}

export function LodgedRecords({ records, isHistory = false, onDownload }: LodgedRecordsProps) {
  if (records.length === 0) return null
  return (
    <div className="space-y-2" aria-label={isHistory ? 'Previously lodged files' : 'Lodged files'}>
      {records.map((record, index) => (
        <RecordBlock key={record.id} record={record} isLatest={index === 0} isHistory={isHistory} onDownload={onDownload} />
      ))}
    </div>
  )
}
